/**
 * Public Stylist Marketplace grid — /stylists
 *
 * Search bar + filter chips + grid of stylist cards. Each card carries a
 * personalized "Style Match %" pill that the backend computes via
 * stylist_service.compute_style_match. The marketplace lives inside the
 * existing bottom-nav Stylist tab per PRD ("MUST NOT become a new tab").
 *
 * Filters mirror the backend GET /stylists query params so the URL is
 * shareable / bookmarkable in a future improvement (currently local state).
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Search, Users, MapPin, BadgeCheck, Star, Loader2, Filter,
  ArrowUpRight, ArrowUpDown, Wand2, LocateFixed,
} from "lucide-react";
import { motion } from "framer-motion";
import {
  SPECIALTY_KEYS, SPECIALTY_LABELS, PRICE_TIERS, prettySpecialty, StyleMatchPill,
} from "@/pages/stylists/_shared";

const SORTS = [
  { key: "match", label: "Best match" },
  { key: "featured", label: "Featured" },
  { key: "experience", label: "Most experienced" },
  { key: "newest", label: "New" },
];

export default function Stylists() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [gender, setGender] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [priceTier, setPriceTier] = useState("");
  const [minExp, setMinExp] = useState(0);
  const [minRating, setMinRating] = useState(0);
  const [language, setLanguage] = useState("");
  const [occasion, setOccasion] = useState("");
  const [city, setCity] = useState("");
  const [sort, setSort] = useState("match");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim().length >= 2) params.set("q", q.trim());
      if (gender) params.set("gender", gender);
      if (specialty) params.set("specialty", specialty);
      if (priceTier) params.set("price_tier", priceTier);
      if (minExp) params.set("min_experience", String(minExp));
      if (minRating) params.set("min_rating", String(minRating));
      if (language) params.set("language", language);
      if (occasion) params.set("occasion", occasion);
      if (city) params.set("city", city);
      if (sort) params.set("sort", sort);
      const { data } = await api.get(`/stylists?${params.toString()}`);
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [q, gender, specialty, priceTier, minExp, minRating, language, occasion, city, sort]);

  useEffect(() => {
    load();
  }, [load]);

  const useMyCity = () => {
    // First try the user's saved profile city — one tap, no permission prompt.
    if (user?.city) {
      setCity(user.city);
      return;
    }
    // Fall back to browser geolocation + free Nominatim reverse-geocode.
    // Wrapped in a defensive try — geolocation is often unavailable in the
    // Capacitor WebView without permissions, and Nominatim may rate-limit.
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const r = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&zoom=10&addressdetails=1`,
          );
          const j = await r.json();
          const city = j?.address?.city || j?.address?.town || j?.address?.village || j?.address?.state_district;
          if (city) setCity(city);
        } catch { /* silent */ }
      },
      () => { /* denied — leave field blank */ },
      { timeout: 6000, maximumAge: 60_000 },
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12 lg:py-16" data-testid="stylists-marketplace">
      {/* Header */}
      <div className="flex items-center justify-end gap-6 flex-wrap">
        <Link to="/stylist-studio">
          <Button
            data-testid="become-stylist-btn"
            variant="outline"
            className="rounded-none uppercase tracking-[0.18em] text-[10px] h-10 px-4 gap-2"
          >
            <Wand2 size={13} /> Become a stylist
          </Button>
        </Link>
      </div>

      {/* Compact search + icon toolbar */}
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
          <Input
            data-testid="stylist-search-input"
            placeholder="Search stylists, city, specialty…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-10 rounded-none text-sm"
          />
        </div>
        <IconMenu
          testid="stylist-sort"
          icon={<ArrowUpDown size={14} />}
          value={sort}
          options={SORTS.map((s) => ({ value: s.key, label: s.label }))}
          onChange={setSort}
          activeLabel="Sort"
        />
        <IconToggle
          testid="stylist-filters-toggle"
          icon={<Filter size={14} />}
          active={filtersOpen || hasActiveFilters(gender, specialty, priceTier, minExp, occasion, language, minRating)}
          onClick={() => setFiltersOpen((v) => !v)}
          label={filtersOpen ? "Hide" : "Filters"}
        />
      </div>

      {/* Filter panel */}
      {filtersOpen && (
        <div className="mt-3 border border-border p-4 space-y-4 bg-background/40" data-testid="stylist-filter-panel">
          <FilterRow label="For">
            {["", "male", "female"].map((g) => (
              <Chip key={g || "all"} active={gender === g} onClick={() => setGender(g)} testid={`filter-gender-${g || "all"}`}>
                {g === "" ? "Everyone" : g === "male" ? "Men" : "Women"}
              </Chip>
            ))}
          </FilterRow>
          <FilterRow label="Price tier">
            {["", ...PRICE_TIERS].map((t) => (
              <Chip key={t || "any"} active={priceTier === t} onClick={() => setPriceTier(t)} testid={`filter-tier-${t || "any"}`}>
                {t === "" ? "Any" : t.charAt(0).toUpperCase() + t.slice(1)}
              </Chip>
            ))}
          </FilterRow>
          <FilterRow label="Experience">
            {[0, 3, 5, 10].map((y) => (
              <Chip key={y} active={minExp === y} onClick={() => setMinExp(y)} testid={`filter-exp-${y}`}>
                {y === 0 ? "Any" : `${y}+ yrs`}
              </Chip>
            ))}
          </FilterRow>
          <FilterRow label="Rating">
            {[0, 3, 4, 4.5].map((r) => (
              <Chip key={r} active={minRating === r} onClick={() => setMinRating(r)} testid={`filter-rating-${r}`}>
                {r === 0 ? "Any" : `${r}+ ★`}
              </Chip>
            ))}
          </FilterRow>
          <FilterRow label="Language">
            {["", "English", "Hindi", "Marathi", "Tamil", "Bengali", "Punjabi"].map((lng) => (
              <Chip key={lng || "any"} active={language === lng} onClick={() => setLanguage(lng)} testid={`filter-lang-${lng || "any"}`}>
                {lng || "Any"}
              </Chip>
            ))}
          </FilterRow>
          <FilterRow label="Location">
            <div className="flex-1 min-w-[200px] flex items-center gap-2">
              <Input
                data-testid="filter-city-input"
                placeholder="City (e.g., Mumbai)"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="h-9 rounded-none text-sm flex-1"
              />
              <button
                type="button"
                data-testid="filter-nearby-btn"
                onClick={useMyCity}
                className="text-[10px] uppercase tracking-[0.18em] px-3 h-9 border border-border hover:border-foreground inline-flex items-center gap-1.5 whitespace-nowrap"
                title="Use my city"
              >
                <LocateFixed size={12} /> Nearby
              </button>
            </div>
          </FilterRow>
          <FilterRow label="Occasion">
            <Chip active={occasion === ""} onClick={() => setOccasion("")} testid="filter-occ-any">Any</Chip>
            {["wedding", "office", "party", "vacation", "traditional", "date_night", "editorial"].map((o) => (
              <Chip key={o} active={occasion === o} onClick={() => setOccasion(o)} testid={`filter-occ-${o}`}>
                {SPECIALTY_LABELS[o] || o}
              </Chip>
            ))}
          </FilterRow>
          <FilterRow label="Specialty">
            <Chip active={specialty === ""} onClick={() => setSpecialty("")} testid="filter-spec-any">Any</Chip>
            {SPECIALTY_KEYS.slice(0, 12).map((s) => (
              <Chip key={s} active={specialty === s} onClick={() => setSpecialty(s)} testid={`filter-spec-${s}`}>
                {SPECIALTY_LABELS[s]}
              </Chip>
            ))}
          </FilterRow>
          {hasActiveFilters(gender, specialty, priceTier, minExp, occasion, language, minRating) && (
            <div className="pt-2 border-t border-border">
              <button
                onClick={() => { setGender(""); setSpecialty(""); setPriceTier(""); setMinExp(0); setOccasion(""); setLanguage(""); setMinRating(0); }}
                data-testid="stylists-clear-all-filters"
                className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      )}

      {/* Grid */}
      <div className="mt-10">
        {loading ? (
          <div className="py-24 flex justify-center">
            <Loader2 className="animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <EmptyState hasFilters={!!(q || gender || specialty || priceTier || minExp || occasion || language || minRating || city)} onReset={() => {
            setQ(""); setGender(""); setSpecialty(""); setPriceTier(""); setMinExp(0);
            setOccasion(""); setLanguage(""); setMinRating(0); setCity("");
          }} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="stylists-grid">
            {items.map((s, i) => <StylistCard key={s.id} stylist={s} i={i} authed={!!user} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function FilterRow({ label, children }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground w-24 shrink-0">{label}</span>
      <div className="flex gap-2 flex-wrap flex-1">{children}</div>
    </div>
  );
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 border transition-colors ${
        active
          ? "bg-accent text-accent-foreground border-accent"
          : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

function IconMenu({ testid, icon, value, options, onChange, activeLabel }) {
  const label = options.find((o) => o.value === value)?.label || activeLabel;
  return (
    <label className="relative inline-flex items-center gap-1.5 h-10 border border-input px-3 cursor-pointer hover:border-foreground">
      {icon}
      <span className="text-[11px] uppercase tracking-[0.18em] hidden sm:inline">{label}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 opacity-0 cursor-pointer"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

function IconToggle({ testid, icon, active, onClick, label }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`h-10 px-3 border inline-flex items-center gap-1.5 transition-colors ${
        active ? "border-accent text-accent" : "border-input text-foreground hover:border-foreground"
      }`}
    >
      {icon}
      <span className="text-[11px] uppercase tracking-[0.18em] hidden sm:inline">{label}</span>
    </button>
  );
}

function hasActiveFilters(gender, specialty, priceTier, minExp, occasion, language, minRating) {
  return !!(gender || specialty || priceTier || minExp || occasion || language || minRating);
}

function EmptyState({ hasFilters, onReset }) {
  return (
    <div className="border border-dashed border-border p-12 text-center" data-testid="stylists-empty">
      <Users size={28} className="mx-auto text-muted-foreground" strokeWidth={1.2} />
      <p className="mt-4 text-sm text-muted-foreground max-w-md mx-auto">
        {hasFilters
          ? "No stylists match those filters yet. Try relaxing them, or check back soon — we're onboarding new stylists every week."
          : "The marketplace is warming up. Verified stylists will appear here shortly."}
      </p>
      {hasFilters && (
        <Button
          data-testid="stylists-clear-filters"
          variant="outline"
          onClick={onReset}
          className="rounded-none mt-6 uppercase tracking-[0.18em] text-[10px] h-10"
        >
          Clear filters
        </Button>
      )}
    </div>
  );
}

function StylistCard({ stylist, i, authed }) {
  const match = stylist.style_match?.score;
  const cover = stylist.cover_url ? fileUrl(stylist.cover_url) : null;
  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(i * 0.04, 0.3) }}
      className="border border-border bg-background hover:border-foreground transition-colors group"
      data-testid={`stylist-card-${stylist.id}`}
    >
      <Link to={`/stylists/${stylist.id}`} className="block">
        <div className="aspect-[4/5] bg-muted relative overflow-hidden">
          {cover ? (
            <img src={cover} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-background">
              <span className="font-serif text-5xl text-muted-foreground/40 tracking-tight">
                {(stylist.display_name || "?").charAt(0)}
              </span>
            </div>
          )}
          {stylist.featured && (
            <span className="absolute top-3 left-3 text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 bg-accent text-accent-foreground">
              Featured
            </span>
          )}
          {authed && typeof match === "number" && (
            <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-2">
              <StyleMatchPill score={match} className="bg-background/85 backdrop-blur-sm" />
              {stylist.rating > 0 && (
                <span className="text-[10px] flex items-center gap-1 bg-background/85 backdrop-blur-sm px-2 py-1 border border-border">
                  <Star size={10} className="fill-current" /> {stylist.rating.toFixed(1)}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="p-5">
          <div className="flex items-center gap-1.5">
            <h3 className="font-serif text-xl leading-tight">{stylist.display_name}</h3>
            {stylist.verification_status === "approved" && (
              <BadgeCheck size={14} className="text-accent shrink-0" />
            )}
          </div>
          {stylist.headline && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{stylist.headline}</p>
          )}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {(stylist.specialties || []).slice(0, 3).map((s) => (
              <span key={s} className="text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 border border-border text-muted-foreground">
                {prettySpecialty(s)}
              </span>
            ))}
          </div>
          <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <MapPin size={11} /> {stylist.city || "—"}
            </span>
            <span className="uppercase tracking-[0.16em] text-[10px]">
              {stylist.experience_years > 0 ? `${stylist.experience_years} yrs` : "New"}
            </span>
          </div>
          <div className="mt-4 flex items-center justify-between text-[11px] uppercase tracking-[0.2em]">
            <span>View profile</span>
            <ArrowUpRight size={13} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </div>
        </div>
      </Link>
    </motion.article>
  );
}
